import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { REGISTER_CONFIG } from "./govConfig";
import { StatusBadge } from "./StatusBadge";
import { RecordDialog } from "./RecordDialog";

export function RegisterTab({ kind, meta }) {
  const cfg = REGISTER_CONFIG[kind];
  const statuses = meta[cfg.statusKey];
  const titleKey = cfg.fields[0].key;
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/governance/${kind}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (form) => {
    try {
      if (edit) await api.patch(`/governance/${kind}/${edit.id}`, form);
      else await api.post(`/governance/${kind}`, form);
      toast.success("Saved and recorded in audit log");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!rows) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-accent" />;

  return (
    <div className="space-y-4" data-testid={`register-${kind}`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-900">{cfg.title}</h2>
          <p className="text-sm text-slate-500">{cfg.subtitle}</p>
        </div>
        <Button onClick={() => { setEdit(null); setOpen(true); }} className="bg-accent font-bold hover:bg-accent/90" data-testid={`${kind}-add-btn`}>
          <Plus className="mr-1.5 h-4 w-4" /> Add entry
        </Button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <Card
            key={r.id}
            onClick={() => { setEdit(r); setOpen(true); }}
            className="flex cursor-pointer flex-col gap-2 border-slate-200 p-4 shadow-sm transition-colors hover:border-accent sm:flex-row sm:items-center sm:justify-between"
            data-testid={`${kind}-row-${r.key || r.id}`}
          >
            <div className="min-w-0">
              <div className="font-heading font-bold text-slate-900">{r[titleKey]}</div>
              <div className="truncate text-xs text-slate-500">{cfg.columns.map((c) => r[c]).filter(Boolean).join(" · ")}</div>
            </div>
            <StatusBadge status={r.status} />
          </Card>
        ))}
      </div>
      <RecordDialog kind={kind} cfg={cfg} statuses={statuses} record={edit} open={open} onOpenChange={setOpen} onSave={save} />
    </div>
  );
}
